import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import SEO from '../components/SEO';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'inquiries'), {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        course: formData.course,
        message: formData.message.trim(),
        status: 'new',
        source: 'contact_form',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        course: '',
        message: '',
      });
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setSubmitError(t('contact.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Kamal Institute",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-97633-03025",
        "contactType": "Customer Service",
        "email": "info@kamalbullion.com",
        "areaServed": "IN",
        "availableLanguage": ["English", "Hindi"]
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Ajmer",
        "addressRegion": "Rajasthan",
        "addressCountry": "IN"
      }
    }
  };

  return (
    <div className="bg-white pb-16">
      <SEO
        title="Contact Kamal Institute - Get in Touch"
        description="Have questions about our courses? Contact Kamal Institute in Ajmer, Rajasthan. Call +91 97633 03025 or email info@kamalbullion.com"
        keywords="contact Kamal Institute, jewellery course inquiry, Ajmer jewellery institute contact, goldsmithing course contact"
        url="/contact"
        structuredData={structuredData}
      />
      {/* Header */}
      <div className="bg-gradient-to-b from-gold-100/20 py-16 pt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {t('contact.title')}
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Contact Info & Form */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.getInTouch')}</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPinIcon className="h-6 w-6 text-gold-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">{t('contact.location')}</h3>
                  <p className="text-gray-600 mt-1">
                    {t('contact.locationAddress').split('\n').map((line, i) => (
                      <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
                    ))}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <EnvelopeIcon className="h-6 w-6 text-gold-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">{t('contact.email')}</h3>
                  <a 
                    href="mailto:info@kamalbullion.com" 
                    className="text-gold-600 hover:text-gold-500 mt-1 block transition-colors"
                  >
                    info@kamalbullion.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <PhoneIcon className="h-6 w-6 text-gold-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">{t('contact.phone')}</h3>
                  <a 
                    href="tel:+919763303025" 
                    className="text-gold-600 hover:text-gold-500 mt-1 block transition-colors"
                  >
                    +91 97633 03025
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10 p-6 bg-gray-50 rounded-2xl">
              <h3 className="font-semibold text-gray-900 mb-3">{t('contact.officeHours')}</h3>
              <p className="text-gray-600 text-sm">
                {t('contact.officeHoursText').split('\n').map((line, i) => (
                  <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
                ))}
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.sendMessage')}</h2>
              
              {submitted && (
                <div className="mb-6 rounded-md bg-green-50 p-4 border border-green-200">
                  <p className="text-sm text-green-800">
                    {t('contact.thankYou')}
                  </p>
                </div>
              )}
              {submitError && (
                <div className="mb-6 rounded-md bg-red-50 p-4 border border-red-200">
                  <p className="text-sm text-red-800">{submitError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold leading-6 text-gray-900">
                      {t('contact.fullName')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-2 block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gold-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold leading-6 text-gray-900">
                      {t('contact.emailLabel')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gold-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold leading-6 text-gray-900">
                      {t('contact.phoneNumber')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gold-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="course" className="block text-sm font-semibold leading-6 text-gray-900">
                      {t('contact.interestedCourse')}
                    </label>
                    <select
                      name="course"
                      id="course"
                      required
                      value={formData.course}
                      onChange={handleChange}
                      className="mt-2 block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-gold-600 sm:text-sm sm:leading-6"
                    >
                      <option value="">{t('contact.selectCourse')}</option>
                      <option value="beginners">{t('contact.beginners')}</option>
                      <option value="intermediate">{t('contact.intermediate')}</option>
                      <option value="expert">{t('contact.expert')}</option>
                    </select>
                  </div>
                </div>

                <div>
                    <label htmlFor="message" className="block text-sm font-semibold leading-6 text-gray-900">
                      {t('contact.message')}
                    </label>
                  <textarea
                    name="message"
                    id="message"
                    rows={4}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-2 block w-full rounded-md border-0 bg-white px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gold-600 sm:text-sm sm:leading-6"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-md bg-gold-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-gold-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? t('contact.sending') : t('contact.sendMessageBtn')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24">
        <div className="mx-auto max-w-2xl text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {t('contact.faq')}
          </h2>
        </div>
        
        <div className="mx-auto max-w-3xl space-y-6">
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('contact.faq1Q')}
            </h3>
            <p className="text-gray-600">
              {t('contact.faq1A')}
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('contact.faq2Q')}
            </h3>
            <p className="text-gray-600">
              {t('contact.faq2A')}
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('contact.faq3Q')}
            </h3>
            <p className="text-gray-600">
              {t('contact.faq3A')}
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('contact.faq4Q')}
            </h3>
            <p className="text-gray-600">
              {t('contact.faq4A')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
